import { Easing, EasingDirection, EasingType } from '@mos/shared/utils'
import { Easing as TEasing } from '@tweenjs/tween.js'

export type EasingFn = (value: number) => number
export type EasingValue = EasingFn | Record<EasingDirection, EasingFn>

export const EasingValues: Record<EasingType, keyof typeof TEasing> = {
	linear: 'Linear',
	sine: 'Sinusoidal',
	circular: 'Circular',
	cubic: 'Cubic',
	bounce: 'Bounce',
	elastic: 'Elastic',
	expo: 'Exponential',
	quadratic: 'Quadratic',
	back: 'Back',
	quintic: 'Quintic',
}

export const NoneEasingValues: { [key in EasingType]?: keyof typeof TEasing } = {
	linear: 'Linear',
}

function isEasingValueFn(value: EasingValue): value is EasingFn {
	return typeof value === 'function'
}

export function isEasingRequiringDirection(type: EasingType): boolean {
	return !NoneEasingValues[type]
}

export function isValidEasingDirection(easing: Easing): boolean {
	return (
		(!isEasingRequiringDirection(easing.type) && !easing.direction) ||
		(isEasingRequiringDirection(easing.type) && !!easing.direction)
	)
}

export function getEasingFn(easing: Easing): EasingFn {
	const value = TEasing[EasingValues[easing.type]] as EasingValue

	if (isEasingValueFn(value)) {
		return value
	}

	if (!easing.direction) {
		// eg. tween.js's Easing.Linear is a group ({In, Out, InOut, None}), not a bare function — types marked as
		// not requiring a direction (see NoneEasingValues) resolve through their "None" variant instead.
		const none = (value as Record<string, EasingFn | undefined>)['None']
		if (none) return none

		throw new Error(`Easing ${easing} requires a direction`)
	}

	return value[easing.direction]
}

export interface EasingSvgPathOptions {
	samples?: number
	size?: number
}

// Builds an SVG path `d` string previewing an easing curve, for use in a small `<svg viewBox="0 0 size size">`.
// Normalizes per-curve (not against a shared [0,1] scale) so curves that overshoot it (back, elastic) still
// fill their own preview box instead of being clipped or shrunk to make room for an overshoot most curves don't have.
export function getEasingSvgPath(fn: EasingFn, options?: EasingSvgPathOptions): string {
	const samples = options?.samples ?? 32
	const size = options?.size ?? 100

	const values = Array.from({ length: samples }, (_, i) => fn(i / (samples - 1)))
	const min = Math.min(...values)
	const max = Math.max(...values)
	const range = max - min || 1

	return values
		.map((value, i) => {
			const x = (i / (samples - 1)) * size
			const y = size - ((value - min) / range) * size
			return `${i === 0 ? 'M' : 'L'}${x},${y}`
		})
		.join(' ')
}
